/* ========================================================================== 
   AL FAJR PRODUCT GALLERY v2
   Production gallery controller for Shopify OS 2.0 / Ella
   ========================================================================== */

(() => {
  'use strict';

  const COMPONENT_NAME = 'al-fajr-gallery';
  const SELECTOR = {
    viewport: '[data-alfajr-gallery-viewport]',
    track: '[data-alfajr-gallery-track]',
    slide: '[data-alfajr-gallery-slide]',
    thumbnail: '[data-alfajr-gallery-thumbnail]',
    thumbnailRail: '[data-alfajr-gallery-thumbnails]',
    dot: '[data-alfajr-gallery-dot]',
    previous: '[data-alfajr-gallery-previous]',
    next: '[data-alfajr-gallery-next]',
    counterCurrent: '[data-alfajr-gallery-counter-current]',
    status: '[data-alfajr-gallery-status]',
    progressBar: '[data-alfajr-gallery-progress-bar]',
    zoomTrigger: '[data-alfajr-gallery-zoom-trigger]',
    deferred: '[data-alfajr-gallery-deferred]',
    deferredTrigger: '[data-alfajr-gallery-deferred-trigger]',
    deferredTemplate: 'template[data-alfajr-gallery-deferred-template]',
    deferredContent: '[data-alfajr-gallery-deferred-content]',
    variantData: 'script[data-alfajr-gallery-variants]',
    lightbox: '[data-alfajr-gallery-lightbox]',
    lightboxImage: '[data-alfajr-gallery-lightbox-image]',
    lightboxStage: '[data-alfajr-gallery-lightbox-stage]',
    lightboxClose: '[data-alfajr-gallery-lightbox-close]',
    lightboxPrevious: '[data-alfajr-gallery-lightbox-previous]',
    lightboxNext: '[data-alfajr-gallery-lightbox-next]',
    lightboxCurrent: '[data-alfajr-gallery-lightbox-current]',
    lightboxTotal: '[data-alfajr-gallery-lightbox-total]'
  };

  const INTERACTIVE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'video',
    'iframe',
    'model-viewer',
    '[role="button"]',
    '[contenteditable="true"]',
    '.shopify-model-viewer-ui'
  ].join(',');

  const VARIANT_EVENT_NAMES = [
    'variant:change',
    'variant:changed',
    'product:variant-change',
    'product:variant-changed',
    'theme:variant:change',
    'theme:variant:changed',
    'shopify:variant:change',
    'shopify:variant:changed'
  ];

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const parseBoolean = (value, fallback = false) => {
    if (value === null || value === undefined || value === '') return fallback;
    return value === true || value === 'true' || value === '1';
  };

  const parseNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const requestDoubleAnimationFrame = (callback) => {
    requestAnimationFrame(() => requestAnimationFrame(callback));
  };

  class AlFajrGallery extends HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.destroyed = false;
      this.abortController = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.animationHandles = [];
      this.imageLoadPromises = new Map();
      this.variantMap = new Map();
      this.pauseReasons = new Set();
      this.autoplayTimer = null;
      this.interactionResumeTimer = null;
      this.resizeFrame = null;
      this.dragFrame = null;
      this.pendingVariantTimer = null;
      this.pendingVariantFrame = null;
      this.lastSynchronizedVariantId = null;
      this.lastFocusedElement = null;
      this.lightboxIndex = -1;
      this.lightboxPortalPlaceholder = null;
      this.lightboxOriginalParent = null;
      this.lightboxOriginalNextSibling = null;
      this.isIntersecting = true;
      this.isDocumentVisible = !document.hidden;
      this.isAnimating = false;
      this.suppressClickUntil = 0;

      this.pointer = {
        active: false,
        id: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        startTime: 0,
        moved: false,
        horizontal: false
      };

      this.bound = {
        onPreviousClick: this.onPreviousClick.bind(this),
        onNextClick: this.onNextClick.bind(this),
        onControlPointerDown: this.onControlPointerDown.bind(this),
        onThumbnailClick: this.onThumbnailClick.bind(this),
        onDotClick: this.onDotClick.bind(this),
        onViewportKeyDown: this.onViewportKeyDown.bind(this),
        onViewportPointerDown: this.onViewportPointerDown.bind(this),
        onViewportPointerMove: this.onViewportPointerMove.bind(this),
        onViewportPointerUp: this.onViewportPointerUp.bind(this),
        onViewportPointerCancel: this.onViewportPointerCancel.bind(this),
        onViewportClick: this.onViewportClick.bind(this),
        onZoomTriggerClick: this.onZoomTriggerClick.bind(this),
        onZoomPointerMove: this.onZoomPointerMove.bind(this),
        onZoomPointerLeave: this.onZoomPointerLeave.bind(this),
        onDeferredClick: this.onDeferredClick.bind(this),
        onMouseEnter: this.onMouseEnter.bind(this),
        onMouseLeave: this.onMouseLeave.bind(this),
        onFocusIn: this.onFocusIn.bind(this),
        onFocusOut: this.onFocusOut.bind(this),
        onVisibilityChange: this.onVisibilityChange.bind(this),
        onDocumentKeyDown: this.onDocumentKeyDown.bind(this),
        onVariantEvent: this.onVariantEvent.bind(this),
        onProductFormChange: this.onProductFormChange.bind(this),
        onPopState: this.onPopState.bind(this),
        onLightboxCloseClick: this.onLightboxCloseClick.bind(this),
        onLightboxPreviousClick: this.onLightboxPreviousClick.bind(this),
        onLightboxNextClick: this.onLightboxNextClick.bind(this),
        onLightboxClick: this.onLightboxClick.bind(this),
        onLightboxNativeClose: this.onLightboxNativeClose.bind(this),
        onLightboxStagePointerMove: this.onLightboxStagePointerMove.bind(this)
      };
    }

    connectedCallback() {
      if (this.initialized || this.destroyed) return;
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    initialize() {
      this.cacheElements();

      if (!this.viewport || this.slides.length === 0) {
        this.classList.add('is-disabled');
        return;
      }

      this.initialized = true;
      this.abortController = new AbortController();
      this.config = this.readConfiguration();
      this.activeIndex = this.resolveInitialIndex();
      this.previousIndex = this.activeIndex;
      this.imageSlideIndices = this.slides
        .map((slide, index) => (slide.dataset.mediaType === 'image' ? index : -1))
        .filter((index) => index >= 0);

      this.readVariantData();
      this.bindEvents();
      this.setupObservers();
      this.applyInitialState();
      this.preloadAround(this.activeIndex);
      this.startAutoplay();

      this.dispatchEvent(
        new CustomEvent('alfajr-gallery:ready', {
          bubbles: true,
          detail: {
            productId: this.config.productId,
            mediaCount: this.slides.length,
            activeIndex: this.activeIndex
          }
        })
      );
    }

    cacheElements() {
      this.viewport = this.querySelector(SELECTOR.viewport);
      this.track = this.querySelector(SELECTOR.track);
      this.slides = Array.from(this.querySelectorAll(SELECTOR.slide));
      this.thumbnails = Array.from(this.querySelectorAll(SELECTOR.thumbnail));
      this.thumbnailRail = this.querySelector(SELECTOR.thumbnailRail);
      this.dots = Array.from(this.querySelectorAll(SELECTOR.dot));
      this.previousButton = this.querySelector(SELECTOR.previous);
      this.nextButton = this.querySelector(SELECTOR.next);
      this.counterCurrent = this.querySelector(SELECTOR.counterCurrent);
      this.status = this.querySelector(SELECTOR.status);
      this.progressBar = this.querySelector(SELECTOR.progressBar);
      this.variantDataElement = this.querySelector(SELECTOR.variantData);
      this.lightbox = this.querySelector(SELECTOR.lightbox);
      this.lightboxImage = this.querySelector(SELECTOR.lightboxImage);
      this.lightboxStage = this.querySelector(SELECTOR.lightboxStage);
      this.lightboxCloseButton = this.querySelector(SELECTOR.lightboxClose);
      this.lightboxPreviousButton = this.querySelector(SELECTOR.lightboxPrevious);
      this.lightboxNextButton = this.querySelector(SELECTOR.lightboxNext);
      this.lightboxCurrent = this.querySelector(SELECTOR.lightboxCurrent);
      this.lightboxTotal = this.querySelector(SELECTOR.lightboxTotal);
    }

    readConfiguration() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      return {
        productId: String(this.dataset.productId || ''),
        sectionId: String(this.dataset.sectionId || ''),
        loop: parseBoolean(this.dataset.loop, true),
        autoplay: parseBoolean(this.dataset.autoplay, false) && !reducedMotion,
        autoplayDelay: clamp(parseNumber(this.dataset.autoplayDelay, 5000), 2500, 30000),
        interactionResumeDelay: clamp(parseNumber(this.dataset.interactionResumeDelay, 8000), 3000, 60000),
        transitionDuration: reducedMotion ? 0 : clamp(parseNumber(this.dataset.transitionDuration, 420), 120, 1000),
        swipeThreshold: clamp(parseNumber(this.dataset.swipeThreshold, 48), 24, 140),
        velocityThreshold: clamp(parseNumber(this.dataset.velocityThreshold, 0.35), 0.15, 1.5),
        hoverZoom: parseBoolean(this.dataset.hoverZoom, true),
        lightbox: parseBoolean(this.dataset.lightbox, true),
        announceChanges: parseBoolean(this.dataset.announceChanges, true),
        reducedMotion
      };
    }

    resolveInitialIndex() {
      const explicitlyActive = this.slides.findIndex(
        (slide) => slide.classList.contains('is-active') || slide.getAttribute('aria-hidden') === 'false'
      );

      if (explicitlyActive >= 0) return explicitlyActive;

      const selectedMediaId = String(this.dataset.selectedMediaId || '');
      const selectedIndex = this.slides.findIndex(
        (slide) => String(slide.dataset.mediaId || '') === selectedMediaId
      );

      return selectedIndex >= 0 ? selectedIndex : 0;
    }

    readVariantData() {
      if (!this.variantDataElement) return;

      try {
        const variants = JSON.parse(this.variantDataElement.textContent || '[]');

        variants.forEach((variant) => {
          const id = String(variant.id || '');
          if (!id) return;

          this.variantMap.set(id, {
            id,
            mediaId: variant.mediaId ? String(variant.mediaId) : null,
            available: Boolean(variant.available)
          });
        });
      } catch (error) {
        console.warn('[AL FAJR Gallery] Unable to parse variant media data.', error);
      }
    }

    bindEvents() {
      const signal = this.abortController.signal;
      const listen = (target, type, handler, options = {}) => {
        if (!target) return;
        target.addEventListener(type, handler, { ...options, signal });
      };

      listen(this.previousButton, 'pointerdown', this.bound.onControlPointerDown);
      listen(this.nextButton, 'pointerdown', this.bound.onControlPointerDown);
      listen(this.previousButton, 'click', this.bound.onPreviousClick);
      listen(this.nextButton, 'click', this.bound.onNextClick);

      this.thumbnails.forEach((thumbnail) => {
        listen(thumbnail, 'pointerdown', this.bound.onControlPointerDown);
        listen(thumbnail, 'click', this.bound.onThumbnailClick);
      });

      this.dots.forEach((dot) => {
        listen(dot, 'pointerdown', this.bound.onControlPointerDown);
        listen(dot, 'click', this.bound.onDotClick);
      });

      listen(this.viewport, 'keydown', this.bound.onViewportKeyDown);
      listen(this.viewport, 'pointerdown', this.bound.onViewportPointerDown);
      listen(this.viewport, 'pointermove', this.bound.onViewportPointerMove);
      listen(this.viewport, 'pointerup', this.bound.onViewportPointerUp);
      listen(this.viewport, 'pointercancel', this.bound.onViewportPointerCancel);
      listen(this.viewport, 'lostpointercapture', this.bound.onViewportPointerCancel);
      listen(this.viewport, 'click', this.bound.onViewportClick);

      this.querySelectorAll(SELECTOR.zoomTrigger).forEach((trigger) => {
        listen(trigger, 'pointerdown', this.bound.onControlPointerDown);
        listen(trigger, 'click', this.bound.onZoomTriggerClick);
        listen(trigger, 'pointermove', this.bound.onZoomPointerMove);
        listen(trigger, 'pointerleave', this.bound.onZoomPointerLeave);
      });

      this.querySelectorAll(SELECTOR.deferredTrigger).forEach((trigger) => {
        listen(trigger, 'pointerdown', this.bound.onControlPointerDown);
        listen(trigger, 'click', this.bound.onDeferredClick);
      });

      listen(this, 'mouseenter', this.bound.onMouseEnter);
      listen(this, 'mouseleave', this.bound.onMouseLeave);
      listen(this, 'focusin', this.bound.onFocusIn);
      listen(this, 'focusout', this.bound.onFocusOut);
      listen(document, 'visibilitychange', this.bound.onVisibilityChange);
      listen(document, 'keydown', this.bound.onDocumentKeyDown);
      listen(window, 'popstate', this.bound.onPopState);

      VARIANT_EVENT_NAMES.forEach((eventName) => {
        listen(document, eventName, this.bound.onVariantEvent, true);
      });

      const productScope = this.closest('[id^="shopify-section-"]') || this.closest('section') || document;
      listen(productScope, 'change', this.bound.onProductFormChange, true);

      listen(this.lightboxCloseButton, 'click', this.bound.onLightboxCloseClick);
      listen(this.lightboxPreviousButton, 'click', this.bound.onLightboxPreviousClick);
      listen(this.lightboxNextButton, 'click', this.bound.onLightboxNextClick);
      listen(this.lightbox, 'click', this.bound.onLightboxClick);
      listen(this.lightbox, 'close', this.bound.onLightboxNativeClose);
      listen(this.lightboxStage, 'pointermove', this.bound.onLightboxStagePointerMove);
    }

    setupObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleAdaptiveHeight());
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', () => this.scheduleAdaptiveHeight(), {
          passive: true,
          signal: this.abortController.signal
        });
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.isIntersecting = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0.15);

            if (this.isIntersecting) {
              this.resumeAutoplay('offscreen');
              this.preloadAround(this.activeIndex);
            } else {
              this.pauseAutoplay('offscreen');
            }
          },
          { threshold: [0, 0.15, 0.5] }
        );

        this.intersectionObserver.observe(this);
      }
    }

    applyInitialState() {
      this.slides.forEach((slide, index) => {
        const active = index === this.activeIndex;
        slide.classList.toggle('is-active', active);
        slide.classList.remove('is-transitioning-out', 'is-drag-neighbor');
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.updateControls();
      this.updateAdaptiveHeight({ immediate: true });
      this.hydrateImage(this.activeIndex);
      this.bindHydratedMedia(this.slides[this.activeIndex]);
    }

    onControlPointerDown(event) {
      event.stopPropagation();
    }

    onPreviousClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.registerInteraction();
      this.previous({ source: 'arrow' });
    }

    onNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.registerInteraction();
      this.next({ source: 'arrow' });
    }

    onThumbnailClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const index = parseNumber(event.currentTarget.dataset.index, -1);
      if (index < 0) return;

      this.registerInteraction();
      this.goTo(index, { source: 'thumbnail' });
    }

    onDotClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const index = parseNumber(event.currentTarget.dataset.index, -1);
      if (index < 0) return;

      this.registerInteraction();
      this.goTo(index, { source: 'dot' });
    }

    onViewportKeyDown(event) {
      if (this.isLightboxOpen()) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.registerInteraction();
          this.previous({ source: 'keyboard' });
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.registerInteraction();
          this.next({ source: 'keyboard' });
          break;
        case 'Home':
          event.preventDefault();
          this.registerInteraction();
          this.goTo(0, { source: 'keyboard' });
          break;
        case 'End':
          event.preventDefault();
          this.registerInteraction();
          this.goTo(this.slides.length - 1, { source: 'keyboard' });
          break;
        case 'Enter':
        case ' ':
          if (event.target === this.viewport && this.config.lightbox) {
            event.preventDefault();
            this.openLightbox(this.activeIndex);
          }
          break;
        default:
          break;
      }
    }

    onViewportPointerDown(event) {
      if (event.button !== 0 || !event.isPrimary || this.slides.length < 2) return;
      if (this.isInteractiveTarget(event.target)) return;

      this.pointer.active = true;
      this.pointer.id = event.pointerId;
      this.pointer.startX = event.clientX;
      this.pointer.startY = event.clientY;
      this.pointer.currentX = event.clientX;
      this.pointer.currentY = event.clientY;
      this.pointer.startTime = performance.now();
      this.pointer.moved = false;
      this.pointer.horizontal = false;

      this.pauseAutoplay('drag');

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture can fail if the browser has already cancelled the pointer.
      }
    }

    onViewportPointerMove(event) {
      if (!this.pointer.active || event.pointerId !== this.pointer.id) return;

      this.pointer.currentX = event.clientX;
      this.pointer.currentY = event.clientY;

      const deltaX = this.pointer.currentX - this.pointer.startX;
      const deltaY = this.pointer.currentY - this.pointer.startY;

      if (!this.pointer.moved && Math.hypot(deltaX, deltaY) > 7) {
        this.pointer.moved = true;
        this.pointer.horizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.08;
      }

      if (!this.pointer.horizontal) return;

      event.preventDefault();
      this.classList.add('is-dragging');
      this.viewport.classList.add('is-pointer-dragging');
      this.scheduleDragTransform(deltaX);
    }

    onViewportPointerUp(event) {
      if (!this.pointer.active || event.pointerId !== this.pointer.id) return;
      this.finishPointerGesture(false);
    }

    onViewportPointerCancel(event) {
      if (!this.pointer.active) return;
      if (event.pointerId !== undefined && event.pointerId !== this.pointer.id) return;
      this.finishPointerGesture(true);
    }

    finishPointerGesture(cancelled) {
      const deltaX = this.pointer.currentX - this.pointer.startX;
      const elapsed = Math.max(performance.now() - this.pointer.startTime, 1);
      const velocity = Math.abs(deltaX) / elapsed;
      const wasHorizontalDrag = this.pointer.moved && this.pointer.horizontal;
      const shouldNavigate =
        !cancelled &&
        wasHorizontalDrag &&
        (Math.abs(deltaX) >= this.config.swipeThreshold || velocity >= this.config.velocityThreshold);

      if (wasHorizontalDrag) {
        this.suppressClickUntil = Date.now() + 350;
      }

      this.clearDragTransform();
      this.pointer.active = false;
      this.pointer.id = null;
      this.pointer.moved = false;
      this.pointer.horizontal = false;
      this.classList.remove('is-dragging');
      this.viewport.classList.remove('is-pointer-dragging');
      this.resumeAutoplay('drag');

      if (shouldNavigate) {
        this.registerInteraction();
        deltaX < 0 ? this.next({ source: 'swipe' }) : this.previous({ source: 'swipe' });
      }
    }

    scheduleDragTransform(deltaX) {
      if (this.dragFrame) cancelAnimationFrame(this.dragFrame);

      this.dragFrame = requestAnimationFrame(() => {
        this.dragFrame = null;
        const activeSlide = this.slides[this.activeIndex];
        if (!activeSlide) return;

        const resistance = this.isAtNonLoopingEdge(deltaX) ? 0.28 : 0.82;
        const translatedX = deltaX * resistance;
        activeSlide.style.transition = 'none';
        activeSlide.style.transform = `translate3d(${translatedX}px, 0, 0)`;
        activeSlide.style.opacity = String(clamp(1 - Math.abs(translatedX) / (this.viewport.clientWidth * 1.5), 0.65, 1));
      });
    }

    clearDragTransform() {
      if (this.dragFrame) {
        cancelAnimationFrame(this.dragFrame);
        this.dragFrame = null;
      }

      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;

      activeSlide.style.transition = '';
      activeSlide.style.transform = '';
      activeSlide.style.opacity = '';
    }

    isAtNonLoopingEdge(deltaX) {
      if (this.config.loop) return false;
      return (this.activeIndex === 0 && deltaX > 0) ||
        (this.activeIndex === this.slides.length - 1 && deltaX < 0);
    }

    isInteractiveTarget(target) {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(INTERACTIVE_SELECTOR));
    }

    onZoomTriggerClick(event) {
      if (Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const trigger = event.currentTarget;
      const slide = trigger.closest(SELECTOR.slide);
      const index = slide ? this.slides.indexOf(slide) : this.activeIndex;

      if (index >= 0 && this.config.lightbox) {
        event.preventDefault();
        event.stopPropagation();
        this.registerInteraction();
        this.openLightbox(index);
      }
    }

    onViewportClick(event) {
      const zoomTrigger = event.target.closest(SELECTOR.zoomTrigger);
      if (!zoomTrigger) return;

      if (Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const slide = zoomTrigger.closest(SELECTOR.slide);
      const index = slide ? this.slides.indexOf(slide) : this.activeIndex;

      if (index >= 0 && this.config.lightbox) {
        event.preventDefault();
        event.stopPropagation();
        this.registerInteraction();
        this.openLightbox(index);
      }
    }

    onZoomPointerMove(event) {
      if (!this.config.hoverZoom || event.pointerType === 'touch') return;
      if (this.pointer.active && this.pointer.moved) return;

      const trigger = event.currentTarget;
      const rect = trigger.getBoundingClientRect();
      const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);

      trigger.style.setProperty('--alfajr-zoom-origin-x', `${x}%`);
      trigger.style.setProperty('--alfajr-zoom-origin-y', `${y}%`);
      trigger.classList.add('is-hover-zooming');
    }

    onZoomPointerLeave(event) {
      const trigger = event.currentTarget;
      trigger.classList.remove('is-hover-zooming');
      trigger.style.removeProperty('--alfajr-zoom-origin-x');
      trigger.style.removeProperty('--alfajr-zoom-origin-y');
    }

    onDeferredClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const slide = event.currentTarget.closest(SELECTOR.slide);
      const index = slide ? this.slides.indexOf(slide) : -1;
      if (index < 0) return;

      this.registerInteraction({ persistent: true });
      this.activateDeferredMedia(index, { play: true });
    }

    onMouseEnter() {
      this.pauseAutoplay('hover');
    }

    onMouseLeave() {
      this.resumeAutoplay('hover');
    }

    onFocusIn() {
      this.pauseAutoplay('focus');
    }

    onFocusOut() {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.resumeAutoplay('focus');
      });
    }

    onVisibilityChange() {
      this.isDocumentVisible = !document.hidden;
      this.isDocumentVisible ? this.resumeAutoplay('hidden') : this.pauseAutoplay('hidden');
    }

    onDocumentKeyDown(event) {
      if (!this.isLightboxOpen()) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.navigateLightbox(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.navigateLightbox(1);
          break;
        default:
          break;
      }
    }

    onVariantEvent(event) {
      if (!this.eventBelongsToThisProduct(event)) return;

      const variantId = this.extractVariantId(event);
      if (variantId) this.synchronizeVariant(variantId);
    }

    onProductFormChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const productForm = target.closest('form[action*="/cart/add"]');
      const isVariantControl = target.matches('[name="id"], [name^="options"], [data-option-value], [data-product-attribute]');
      if (!productForm && !isVariantControl) return;

      this.scheduleVariantSync(productForm || target.closest('form'));
    }

    onPopState() {
      this.scheduleVariantSync(null);
    }

    scheduleVariantSync(form) {
      window.clearTimeout(this.pendingVariantTimer);
      if (this.pendingVariantFrame) cancelAnimationFrame(this.pendingVariantFrame);

      const synchronize = () => {
        this.pendingVariantFrame = null;
        const variantId = this.findCurrentVariantId(form);
        if (variantId) this.synchronizeVariant(variantId);
      };

      // One animation frame gives Ella time to update the hidden variant ID
      // without adding the visible 40 ms delay used by the previous build.
      this.pendingVariantFrame = requestAnimationFrame(synchronize);

      // Some product forms update their hidden variant input asynchronously.
      // This silent fallback catches that case without delaying the first attempt.
      this.pendingVariantTimer = window.setTimeout(synchronize, 90);
    }

    findCurrentVariantId(form) {
      const candidates = [];

      if (form) candidates.push(form.querySelector('[name="id"]'));

      const section = this.closest('[id^="shopify-section-"]') || this.closest('section');
      if (section) {
        candidates.push(section.querySelector('form[action*="/cart/add"] [name="id"]'));
        candidates.push(section.querySelector('[name="id"]'));
      }

      for (const input of candidates) {
        if (input && input.value && this.variantMap.has(String(input.value))) {
          return String(input.value);
        }
      }

      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      return urlVariant && this.variantMap.has(String(urlVariant)) ? String(urlVariant) : null;
    }

    eventBelongsToThisProduct(event) {
      const detail = event.detail || {};
      const detailProductId = detail.productId || detail.product_id || detail.product?.id || detail.variant?.product_id;

      if (detailProductId && this.config.productId) {
        return String(detailProductId).replace(/\D/g, '') === this.config.productId.replace(/\D/g, '');
      }

      if (event.target instanceof Element) {
        const targetSection = event.target.closest('[id^="shopify-section-"]');
        const ownSection = this.closest('[id^="shopify-section-"]');
        if (targetSection && ownSection) return targetSection === ownSection;
      }

      return true;
    }

    extractVariantId(event) {
      const detail = event.detail || {};
      const candidate =
        detail.variant?.id ||
        detail.variantId ||
        detail.variant_id ||
        detail.id ||
        (event.target instanceof HTMLSelectElement && event.target.name === 'id' ? event.target.value : null) ||
        (event.target instanceof HTMLInputElement && event.target.name === 'id' ? event.target.value : null);

      return candidate ? String(candidate) : null;
    }

    synchronizeVariant(variantId) {
      const normalizedVariantId = String(variantId);
      const variant = this.variantMap.get(normalizedVariantId);
      if (!variant || !variant.mediaId) return;

      const index = this.slides.findIndex(
        (slide) => String(slide.dataset.mediaId || '') === String(variant.mediaId)
      );

      this.lastSynchronizedVariantId = normalizedVariantId;
      if (index < 0 || index === this.activeIndex) return;

      // A variant selection is a deliberate user action. Pause autoplay so it
      // cannot move away from the selected color while the customer is looking.
      this.registerInteraction();

      this.goTo(index, {
        source: 'variant',
        announce: false,
        transitionDuration: 140,
        thumbnailScrollBehavior: 'auto',
        adaptiveHeightImmediate: true
      });
    }

    previous(options = {}) {
      if (!this.config.loop && this.activeIndex === 0) return;
      const index = this.normalizeIndex(this.activeIndex - 1);
      this.goTo(index, { ...options, direction: -1 });
    }

    next(options = {}) {
      if (!this.config.loop && this.activeIndex === this.slides.length - 1) return;
      const index = this.normalizeIndex(this.activeIndex + 1);
      this.goTo(index, { ...options, direction: 1 });
    }

    normalizeIndex(index) {
      if (this.config.loop) {
        return (index + this.slides.length) % this.slides.length;
      }

      return clamp(index, 0, this.slides.length - 1);
    }

    goTo(index, options = {}) {
      const nextIndex = this.normalizeIndex(parseNumber(index, this.activeIndex));
      if (nextIndex === this.activeIndex || !this.slides[nextIndex]) return;

      const oldIndex = this.activeIndex;
      const direction = options.direction || this.determineDirection(oldIndex, nextIndex);
      const outgoing = this.slides[oldIndex];
      const incoming = this.slides[nextIndex];

      this.cancelAnimations();
      this.pauseActiveRichMedia(outgoing);
      this.hydrateImage(nextIndex);

      this.previousIndex = oldIndex;
      this.activeIndex = nextIndex;
      this.isAnimating = true;

      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-transitioning-out');
      outgoing.setAttribute('aria-hidden', 'true');

      incoming.classList.add('is-active');
      incoming.classList.remove('is-transitioning-out');
      incoming.setAttribute('aria-hidden', 'false');

      this.animateTransition(outgoing, incoming, direction, options);
      this.updateControls(options);
      this.updateAdaptiveHeight({ immediate: options.adaptiveHeightImmediate === true });
      this.preloadAround(nextIndex);
      this.bindHydratedMedia(incoming);
      this.restartAutoplayCycle();

      if (options.announce !== false) this.announceCurrentSlide();

      this.dispatchEvent(
        new CustomEvent('alfajr-gallery:change', {
          bubbles: true,
          detail: {
            productId: this.config.productId,
            mediaId: incoming.dataset.mediaId,
            mediaType: incoming.dataset.mediaType,
            index: nextIndex,
            previousIndex: oldIndex,
            source: options.source || 'api'
          }
        })
      );
    }

    determineDirection(fromIndex, toIndex) {
      if (!this.config.loop) return toIndex > fromIndex ? 1 : -1;

      const directDistance = toIndex - fromIndex;
      const wrappedDistance = directDistance > 0
        ? directDistance - this.slides.length
        : directDistance + this.slides.length;

      return Math.abs(directDistance) <= Math.abs(wrappedDistance)
        ? Math.sign(directDistance) || 1
        : Math.sign(wrappedDistance) || 1;
    }

    animateTransition(outgoing, incoming, direction, options = {}) {
      const requestedDuration = parseNumber(options.transitionDuration, this.config.transitionDuration);
      const duration = this.config.reducedMotion ? 0 : clamp(requestedDuration, 0, 1000);
      const distance = Math.min(Math.max(this.viewport.clientWidth * 0.055, 20), 46);
      const outgoingX = direction > 0 ? -distance : distance;
      const incomingX = direction > 0 ? distance : -distance;

      if (duration === 0 || typeof incoming.animate !== 'function') {
        outgoing.classList.remove('is-transitioning-out');
        this.isAnimating = false;
        return;
      }

      const timing = {
        duration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both'
      };

      const outgoingAnimation = outgoing.animate(
        [
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
          { opacity: 0, transform: `translate3d(${outgoingX}px, 0, 0)` }
        ],
        timing
      );

      const incomingAnimation = incoming.animate(
        [
          { opacity: 0, transform: `translate3d(${incomingX}px, 0, 0)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ],
        timing
      );

      this.animationHandles.push(outgoingAnimation, incomingAnimation);

      Promise.allSettled([outgoingAnimation.finished, incomingAnimation.finished]).then(() => {
        outgoing.classList.remove('is-transitioning-out');
        outgoing.getAnimations().forEach((animation) => animation.cancel());
        incoming.getAnimations().forEach((animation) => animation.cancel());
        this.animationHandles = [];
        this.isAnimating = false;
      });
    }

    cancelAnimations() {
      this.animationHandles.forEach((animation) => {
        try {
          animation.cancel();
        } catch (error) {
          // Animation may already have completed.
        }
      });

      this.animationHandles = [];
      this.slides.forEach((slide) => slide.classList.remove('is-transitioning-out'));
      this.isAnimating = false;
    }

    updateControls(options = {}) {
      this.thumbnails.forEach((thumbnail, index) => {
        const active = index === this.activeIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-selected', active ? 'true' : 'false');
        thumbnail.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.dots.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        dot.setAttribute('tabindex', active ? '0' : '-1');
      });

      if (this.counterCurrent) this.counterCurrent.textContent = String(this.activeIndex + 1);

      if (!this.config.loop) {
        const atBeginning = this.activeIndex === 0;
        const atEnd = this.activeIndex === this.slides.length - 1;
        this.setButtonDisabled(this.previousButton, atBeginning);
        this.setButtonDisabled(this.nextButton, atEnd);
      }

      const activeThumbnail = this.thumbnails[this.activeIndex];
      if (activeThumbnail) {
        const behavior = options.thumbnailScrollBehavior ||
          (this.config.reducedMotion ? 'auto' : 'smooth');
        this.ensureThumbnailVisible(activeThumbnail, behavior);
      }
    }

    ensureThumbnailVisible(thumbnail, behavior = 'auto') {
      const rail = this.thumbnailRail;
      if (!rail || !thumbnail) return;

      const railRect = rail.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const safetyGap = 4;
      let nextTop = rail.scrollTop;
      let nextLeft = rail.scrollLeft;

      if (thumbnailRect.top < railRect.top + safetyGap) {
        nextTop -= railRect.top + safetyGap - thumbnailRect.top;
      } else if (thumbnailRect.bottom > railRect.bottom - safetyGap) {
        nextTop += thumbnailRect.bottom - (railRect.bottom - safetyGap);
      }

      if (thumbnailRect.left < railRect.left + safetyGap) {
        nextLeft -= railRect.left + safetyGap - thumbnailRect.left;
      } else if (thumbnailRect.right > railRect.right - safetyGap) {
        nextLeft += thumbnailRect.right - (railRect.right - safetyGap);
      }

      if (Math.abs(nextTop - rail.scrollTop) < 1 && Math.abs(nextLeft - rail.scrollLeft) < 1) return;

      // Scroll only the thumbnail rail. scrollIntoView() can also move the
      // whole product page and was the main source of the sluggish feeling.
      rail.scrollTo({
        top: nextTop,
        left: nextLeft,
        behavior
      });
    }

    setButtonDisabled(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    announceCurrentSlide() {
      if (!this.config.announceChanges || !this.status) return;

      const slide = this.slides[this.activeIndex];
      const label = slide.dataset.mediaLabel || `Media ${this.activeIndex + 1}`;
      this.status.textContent = `${label}, ${this.activeIndex + 1} of ${this.slides.length}`;
    }

    scheduleAdaptiveHeight() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateAdaptiveHeight();
      });
    }

    updateAdaptiveHeight(options = {}) {
      const slide = this.slides[this.activeIndex];
      if (!slide || !this.viewport) return;

      const width = parseNumber(slide.dataset.mediaWidth, 1);
      const height = parseNumber(slide.dataset.mediaHeight, 1);
      const ratio = width > 0 && height > 0 ? width / height : 1;
      const containerWidth = Math.max(this.viewport.getBoundingClientRect().width, 1);
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const tablet = window.matchMedia('(max-width: 989px)').matches;
      const minimum = mobile ? 210 : tablet ? 220 : 240;
      const viewportLimit = window.innerHeight * (mobile ? 0.76 : tablet ? 0.78 : 0.84);
      const hardMaximum = mobile ? 700 : tablet ? 780 : 900;
      const maximum = Math.max(minimum, Math.min(hardMaximum, viewportLimit));
      const paddingAllowance = mobile ? 6 : 12;
      const idealHeight = containerWidth / clamp(ratio, 0.35, 3.5) + paddingAllowance;
      const preferredHeight = Math.round(clamp(idealHeight, minimum, maximum));

      if (options.immediate) this.viewport.style.transition = 'none';

      this.style.setProperty('--alfajr-gallery-preferred-height', `${preferredHeight}px`);
      this.style.setProperty('--alfajr-gallery-current-height', `${preferredHeight}px`);

      if (options.immediate) {
        requestDoubleAnimationFrame(() => {
          if (this.viewport) this.viewport.style.transition = '';
        });
      }
    }

    hydrateImage(index) {
      const slide = this.slides[index];
      const image = slide?.querySelector('.alfajr-gallery__image');
      if (!image) return Promise.resolve(null);

      if (this.imageLoadPromises.has(index)) return this.imageLoadPromises.get(index);

      if (image.dataset.lazyState === 'loaded' || !image.dataset.src) {
        image.dataset.lazyState = 'loaded';
        return this.decodeImage(image);
      }

      image.dataset.lazyState = 'loading';
      const promise = new Promise((resolve) => {
        const finalize = () => {
          image.dataset.lazyState = 'loaded';
          image.removeEventListener('load', finalize);
          image.removeEventListener('error', finalize);
          this.updateNaturalDimensions(index, image);
          resolve(image);
        };

        image.addEventListener('load', finalize, { once: true });
        image.addEventListener('error', finalize, { once: true });

        if (image.dataset.sizes) image.sizes = image.dataset.sizes;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-sizes');

        if (image.complete) finalize();
      });

      this.imageLoadPromises.set(index, promise);
      return promise;
    }

    decodeImage(image) {
      if (!image) return Promise.resolve(null);
      if (typeof image.decode !== 'function') return Promise.resolve(image);
      return image.decode().catch(() => image);
    }

    updateNaturalDimensions(index, image) {
      if (!image.naturalWidth || !image.naturalHeight) return;

      const slide = this.slides[index];
      slide.dataset.mediaWidth = String(image.naturalWidth);
      slide.dataset.mediaHeight = String(image.naturalHeight);

      if (index === this.activeIndex) this.scheduleAdaptiveHeight();
    }

    preloadAround(index) {
      const indices = [index, this.normalizeIndex(index - 1), this.normalizeIndex(index + 1)];
      [...new Set(indices)].forEach((candidate) => this.hydrateImage(candidate));
    }

    activateDeferredMedia(index, options = {}) {
      const slide = this.slides[index];
      const deferred = slide?.querySelector(SELECTOR.deferred);
      if (!deferred || deferred.classList.contains('is-loaded')) return;

      const template = deferred.querySelector(SELECTOR.deferredTemplate);
      const content = deferred.querySelector(SELECTOR.deferredContent);
      if (!template || !content) return;

      deferred.classList.add('is-loading');
      content.appendChild(template.content.cloneNode(true));
      deferred.classList.add('is-loaded');
      deferred.classList.remove('is-loading');
      this.bindHydratedMedia(slide);

      const video = content.querySelector('video');
      if (video && options.play) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }

      const model = content.querySelector('model-viewer');
      if (model && options.play && typeof model.dismissPoster === 'function') {
        model.dismissPoster();
      }
    }

    bindHydratedMedia(slide) {
      if (!slide || slide.dataset.mediaBound === 'true') return;

      const video = slide.querySelector('video');
      const iframe = slide.querySelector('iframe');
      const model = slide.querySelector('model-viewer');

      // Deferred media lives inside an inert <template> until the customer
      // explicitly activates it. Do not mark the slide as bound too early.
      if (!video && !iframe && !model) return;

      if (video) {
        video.addEventListener('play', () => this.pauseAutoplay('rich-media'), {
          signal: this.abortController.signal
        });
        video.addEventListener('pause', () => this.resumeAutoplay('rich-media'), {
          signal: this.abortController.signal
        });
        video.addEventListener('ended', () => this.resumeAutoplay('rich-media'), {
          signal: this.abortController.signal
        });
      }

      if (iframe || model) {
        slide.addEventListener('pointerdown', () => this.pauseAutoplay('rich-media'), {
          signal: this.abortController.signal,
          once: false
        });
      }

      if (model) this.initializeModelViewer(model);
      slide.dataset.mediaBound = 'true';
    }

    initializeModelViewer(modelViewer) {
      if (!modelViewer || modelViewer.dataset.alfajrModelInitialized === 'true') return;
      modelViewer.dataset.alfajrModelInitialized = 'true';

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (error) => {
            if (error || !window.Shopify.ModelViewerUI) return;

            try {
              // Shopify's controller adds the expected play/pause and model
              // interaction behavior while preserving native camera controls.
              modelViewer.alfajrModelViewerUI = new window.Shopify.ModelViewerUI(modelViewer);
            } catch (initializationError) {
              console.warn('[AL FAJR Gallery] Model viewer UI could not be initialized.', initializationError);
            }
          }
        }
      ]);
    }

    pauseActiveRichMedia(slide) {
      if (!slide) return;

      slide.querySelectorAll('video').forEach((video) => video.pause());

      slide.querySelectorAll('iframe').forEach((iframe) => {
        if (!iframe.contentWindow) return;
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        iframe.contentWindow.postMessage({ method: 'pause' }, '*');
      });

      this.resumeAutoplay('rich-media');
    }

    registerInteraction(options = {}) {
      this.pauseAutoplay('interaction');
      window.clearTimeout(this.interactionResumeTimer);

      if (!options.persistent) {
        this.interactionResumeTimer = window.setTimeout(() => {
          this.resumeAutoplay('interaction');
        }, this.config.interactionResumeDelay);
      }
    }

    startAutoplay() {
      if (!this.config.autoplay || this.slides.length < 2) return;
      this.restartAutoplayCycle();
    }

    restartAutoplayCycle() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.classList.remove('is-autoplay-running');

      if (!this.canAutoplay()) {
        this.classList.add('is-autoplay-paused');
        return;
      }

      this.classList.remove('is-autoplay-paused');
      void this.offsetWidth;
      this.style.setProperty('--alfajr-autoplay-delay', `${this.config.autoplayDelay}ms`);
      this.classList.add('is-autoplay-running');

      this.autoplayTimer = window.setTimeout(() => {
        this.next({ source: 'autoplay' });
      }, this.config.autoplayDelay);
    }

    canAutoplay() {
      return this.config.autoplay &&
        this.slides.length > 1 &&
        this.pauseReasons.size === 0 &&
        this.isIntersecting &&
        this.isDocumentVisible &&
        !this.isLightboxOpen();
    }

    pauseAutoplay(reason) {
      if (!reason) return;
      this.pauseReasons.add(reason);
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.classList.remove('is-autoplay-running');
      this.classList.add('is-autoplay-paused');
    }

    resumeAutoplay(reason) {
      if (!reason) return;
      this.pauseReasons.delete(reason);
      this.restartAutoplayCycle();
    }

    moveLightboxToBody() {
      if (!this.lightbox || this.lightbox.parentElement === document.body) return;

      this.lightboxOriginalParent = this.lightbox.parentNode;
      this.lightboxOriginalNextSibling = this.lightbox.nextSibling;
      this.lightboxPortalPlaceholder = document.createComment('AL FAJR gallery lightbox portal');
      this.lightboxOriginalParent?.insertBefore(this.lightboxPortalPlaceholder, this.lightbox);
      document.body.appendChild(this.lightbox);
    }

    restoreLightboxFromBody() {
      if (!this.lightbox || !this.lightboxOriginalParent) return;

      if (this.lightboxPortalPlaceholder?.parentNode === this.lightboxOriginalParent) {
        this.lightboxOriginalParent.insertBefore(this.lightbox, this.lightboxPortalPlaceholder);
        this.lightboxPortalPlaceholder.remove();
      } else if (this.lightboxOriginalNextSibling?.parentNode === this.lightboxOriginalParent) {
        this.lightboxOriginalParent.insertBefore(this.lightbox, this.lightboxOriginalNextSibling);
      } else if (this.lightboxOriginalParent.isConnected) {
        this.lightboxOriginalParent.appendChild(this.lightbox);
      }

      this.lightboxPortalPlaceholder = null;
      this.lightboxOriginalParent = null;
      this.lightboxOriginalNextSibling = null;
    }

    openLightbox(index) {
      if (!this.config.lightbox || !this.lightbox || !this.lightboxImage) return;
      if (!this.imageSlideIndices.includes(index)) return;

      this.lastFocusedElement = document.activeElement;
      this.lightboxIndex = index;
      this.updateLightboxImage(index);
      this.pauseAutoplay('lightbox');
      this.moveLightboxToBody();

      document.documentElement.classList.add('alfajr-gallery-lightbox-open');
      document.body.classList.add('alfajr-gallery-lightbox-open');
      this.lightbox.classList.add('is-opening');

      let openedAsModal = false;

      if (typeof this.lightbox.showModal === 'function') {
        try {
          if (!this.lightbox.open) this.lightbox.showModal();
          openedAsModal = this.lightbox.open;
        } catch (error) {
          console.warn('[AL FAJR Gallery] Native dialog failed; using fullscreen fallback.', error);
        }
      }

      if (!openedAsModal) {
        this.lightbox.setAttribute('open', '');
        this.lightbox.classList.add('is-fallback-open');
      }

      requestDoubleAnimationFrame(() => {
        this.lightbox?.classList.remove('is-opening');
        this.lightbox?.classList.add('is-visible');
        this.lightboxCloseButton?.focus({ preventScroll: true });
      });
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.classList.remove('is-visible');

      if (typeof this.lightbox.close === 'function' && this.lightbox.open && !this.lightbox.classList.contains('is-fallback-open')) {
        this.lightbox.close();
      } else {
        this.lightbox.removeAttribute('open');
        this.lightbox.classList.remove('is-fallback-open');
        this.finishLightboxClose();
      }
    }

    finishLightboxClose() {
      this.lightbox?.classList.remove('is-opening', 'is-visible', 'is-zoomed', 'is-fallback-open');
      this.lightbox?.removeAttribute('open');
      this.lightboxImage?.style.removeProperty('--alfajr-lightbox-scale');
      this.lightboxImage?.style.removeProperty('--alfajr-lightbox-origin-x');
      this.lightboxImage?.style.removeProperty('--alfajr-lightbox-origin-y');
      document.documentElement.classList.remove('alfajr-gallery-lightbox-open');
      document.body.classList.remove('alfajr-gallery-lightbox-open');
      this.restoreLightboxFromBody();
      this.resumeAutoplay('lightbox');

      if (this.lastFocusedElement instanceof HTMLElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    onLightboxCloseClick(event) {
      event.preventDefault();
      this.closeLightbox();
    }

    onLightboxPreviousClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.navigateLightbox(-1);
    }

    onLightboxNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.navigateLightbox(1);
    }

    onLightboxClick(event) {
      if (event.target === this.lightbox) {
        this.closeLightbox();
        return;
      }

      if (event.target === this.lightboxStage || event.target === this.lightboxImage) {
        this.toggleLightboxZoom(event);
      }
    }

    onLightboxNativeClose() {
      this.finishLightboxClose();
    }

    navigateLightbox(direction) {
      if (this.imageSlideIndices.length < 2) return;

      const currentPosition = this.imageSlideIndices.indexOf(this.lightboxIndex);
      const nextPosition = (currentPosition + direction + this.imageSlideIndices.length) % this.imageSlideIndices.length;
      const nextIndex = this.imageSlideIndices[nextPosition];

      this.lightboxIndex = nextIndex;
      this.updateLightboxImage(nextIndex);
      this.goTo(nextIndex, {
        source: 'lightbox',
        direction,
        announce: false
      });
    }

    updateLightboxImage(index) {
      const slide = this.slides[index];
      if (!slide || !this.lightboxImage) return;

      const sourceImage = slide.querySelector('.alfajr-gallery__image');
      const source = slide.dataset.lightboxSrc || sourceImage?.dataset.src || sourceImage?.currentSrc || sourceImage?.src;
      const alt = sourceImage?.alt || slide.dataset.mediaLabel || '';
      const width = parseNumber(
        slide.dataset.mediaWidth,
        sourceImage?.naturalWidth || sourceImage?.width || 1600
      );
      const height = parseNumber(
        slide.dataset.mediaHeight,
        sourceImage?.naturalHeight || sourceImage?.height || 1600
      );

      this.lightbox.classList.remove('is-zoomed');
      this.lightboxImage.style.removeProperty('--alfajr-lightbox-scale');
      this.lightboxImage.src = source || '';
      this.lightboxImage.alt = alt;
      this.lightboxImage.setAttribute('width', String(Math.max(1, Math.round(width))));
      this.lightboxImage.setAttribute('height', String(Math.max(1, Math.round(height))));

      const imagePosition = this.imageSlideIndices.indexOf(index);
      if (this.lightboxCurrent) this.lightboxCurrent.textContent = String(imagePosition + 1);
      if (this.lightboxTotal) this.lightboxTotal.textContent = String(this.imageSlideIndices.length);

      const hideNavigation = this.imageSlideIndices.length < 2;
      if (this.lightboxPreviousButton) this.lightboxPreviousButton.hidden = hideNavigation;
      if (this.lightboxNextButton) this.lightboxNextButton.hidden = hideNavigation;
    }

    toggleLightboxZoom(event) {
      if (!this.lightbox || !this.lightboxImage) return;

      const zoomed = this.lightbox.classList.toggle('is-zoomed');
      this.lightboxImage.style.setProperty('--alfajr-lightbox-scale', zoomed ? '2' : '1');

      if (zoomed) this.updateLightboxOrigin(event);
    }

    onLightboxStagePointerMove(event) {
      if (!this.lightbox?.classList.contains('is-zoomed')) return;
      this.updateLightboxOrigin(event);
    }

    updateLightboxOrigin(event) {
      const rect = this.lightboxStage.getBoundingClientRect();
      const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      this.lightboxImage.style.setProperty('--alfajr-lightbox-origin-x', `${x}%`);
      this.lightboxImage.style.setProperty('--alfajr-lightbox-origin-y', `${y}%`);
    }

    isLightboxOpen() {
      return Boolean(this.lightbox && (this.lightbox.open || this.lightbox.classList.contains('is-fallback-open')));
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.interactionResumeTimer);
      window.clearTimeout(this.pendingVariantTimer);
      if (this.pendingVariantFrame) cancelAnimationFrame(this.pendingVariantFrame);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if (this.dragFrame) cancelAnimationFrame(this.dragFrame);

      this.cancelAnimations();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      this.finishLightboxClose();

      if (this.lightbox && !this.contains(this.lightbox) && !this.lightboxOriginalParent?.isConnected) {
        this.lightbox.remove();
      }
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, AlFajrGallery);
  }

  // Public helper for theme integrations that need an explicit variant sync.
  window.AlFajrGallery = window.AlFajrGallery || {
    syncVariant(productId, variantId) {
      document.querySelectorAll(COMPONENT_NAME).forEach((gallery) => {
        if (String(gallery.dataset.productId) === String(productId) && typeof gallery.synchronizeVariant === 'function') {
          gallery.synchronizeVariant(String(variantId));
        }
      });
    },

    goToMedia(productId, mediaId) {
      document.querySelectorAll(COMPONENT_NAME).forEach((gallery) => {
        if (String(gallery.dataset.productId) !== String(productId)) return;
        const index = gallery.slides?.findIndex(
          (slide) => String(slide.dataset.mediaId) === String(mediaId)
        );
        if (index >= 0) gallery.goTo(index, { source: 'external-api' });
      });
    }
  };
})();
